// Active Availability date field.
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Popover from "@mui/material/Popover";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { deDE, enUS as muiEnUS } from "@mui/x-date-pickers/locales";
import { format, isValid, parse } from "date-fns";
import { de, enUS } from "date-fns/locale";

import AppField from "../../design-system/components/AppField";
import AppInput from "../../design-system/components/AppInput";

function parseDate(value) {
  const parsed = parse(String(value || ""), "yyyy-MM-dd", new Date());
  return isValid(parsed) ? parsed : null;
}

function displayDate(value) {
  const parsed = parseDate(value);
  return parsed ? format(parsed, "dd.MM.yyyy") : "";
}

export default function AvailabilityDateField({ label, value, min, required = false, disabled = false, onChange }) {
  const { t, i18n } = useTranslation();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const isGerman = i18n.resolvedLanguage?.startsWith("de") ?? true;
  const calendarLocale = isGerman ? de : enUS;
  const calendarLocaleText = isGerman
    ? deDE.components.MuiLocalizationProvider.defaultProps.localeText
    : muiEnUS.components.MuiLocalizationProvider.defaultProps.localeText;

  function openCalendar() {
    if (!disabled) setOpen(true);
  }

  return (
    <>
      <div
        ref={anchorRef}
        className="availability-date-field"
        onClick={openCalendar}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openCalendar();
          }
        }}
      >
        <AppField label={label} size="md" layout="stacked" required={required}>
          <AppInput
            size="md"
            type="text"
            className="availability-date-field__input"
            value={displayDate(value)}
            placeholder={t("availability.date_placeholder")}
            readOnly
            disabled={disabled}
            aria-haspopup="dialog"
            aria-expanded={open ? "true" : "false"}
          />
        </AppField>
      </div>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          className: "availability-legacy-calendar-popover availability-legacy-date-field-popover",
        }}
      >
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={calendarLocale}
          localeText={calendarLocaleText}
        >
          <DateCalendar
            value={parseDate(value)}
            minDate={parseDate(min)}
            onChange={(date) => {
              if (date && isValid(date)) onChange?.(format(date, "yyyy-MM-dd"));
              setOpen(false);
            }}
          />
        </LocalizationProvider>
      </Popover>
    </>
  );
}
